import React, { useEffect, useRef } from 'react';

import {
  observeElementResize,
  syncCanvasBitmapSize,
} from '#/lib/observe-element-resize';

/**
 * The original SVG gradient, rasterized into a canvas once (and on resize)
 * with the blur applied at draw time, instead of a live inline SVG + CSS blur
 * filter. Live gradients/filters get re-rasterized per tile while the GSAP
 * animation above runs, which causes visible seams/banding in Firefox. A
 * canvas is a static bitmap, so compositing it is deterministic.
 */

// The coordinate space the original inline SVG used (its viewBox). The layout
// fit ("xMidYMid meet") is computed against this box.
const VIEW_WIDTH = 820;
const VIEW_HEIGHT = 1685;

// The ellipse (cx 448.5, cy 1400, rx 1594.5, ry 1196) extends far beyond the
// viewBox. In the original inline SVG that overflow still painted across the
// full element width, so the rasterized image must include the ellipse's full
// extent rather than clipping to the viewBox.
const BLEED_X = -1146; // 448.5 - 1594.5
const BLEED_WIDTH = 3189; // 1594.5 * 2
const BLEED_HEIGHT = 2596; // 1400 + 1196

const GRADIENT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="${BLEED_WIDTH}" height="${BLEED_HEIGHT}" viewBox="${BLEED_X} 0 ${BLEED_WIDTH} ${BLEED_HEIGHT}" fill="none"><ellipse cx="448.5" cy="1400" rx="1594.5" ry="1196" fill="url(#g)"/><defs><radialGradient id="g" cx="0" cy="0" r="1" gradientUnits="objectBoundingBox" gradientTransform="translate(0.547 1.15) rotate(-90) scale(1.427 1.694)"><stop stop-color="#D8FB4B"/><stop offset="0.144231" stop-color="#DFDEFD"/><stop offset="0.25" stop-color="#BFBDFC"/><stop offset="0.399038" stop-color="#5F5BF7"/><stop offset="0.85" stop-color="#1C1C1E"/></radialGradient></defs></svg>`;

export const MobileGradient: React.FC<{
  className?: string;
}> = ({ className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const image = new Image();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(GRADIENT_SVG)}`;

    let painted = false;

    const draw = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h || !image.complete || image.naturalWidth === 0) return;
      // Skip when the bitmap already matches — assigning canvas.width with
      // only min-width CSS grows the element and retriggers this observer.
      const resized = syncCanvasBitmapSize(canvas);
      if (!resized && painted) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      painted = true;

      ctx.filter = `blur(${24 * dpr}px)`;

      // Fit of the original viewBox, matching the original inline SVG's
      // preserveAspectRatio="xMidYMid meet" (contain, centered)
      const scale = Math.min((w * dpr) / VIEW_WIDTH, (h * dpr) / VIEW_HEIGHT);
      const ox = (w * dpr - VIEW_WIDTH * scale) / 2;
      const oy = (h * dpr - VIEW_HEIGHT * scale) / 2;

      // Place the expanded image so its coordinate space lines up with the
      // original viewBox fit; the extra width bleeds past the element edges
      // just like the inline SVG's overflow did
      ctx.drawImage(
        image,
        ox + BLEED_X * scale,
        oy,
        BLEED_WIDTH * scale,
        BLEED_HEIGHT * scale,
      );
    };

    image.onload = draw;
    draw();
    const disconnect = observeElementResize(canvas, draw);
    return () => disconnect();
  }, []);

  // Lock the CSS box so the bitmap size cannot drive layout. min-w-full
  // alone is a floor, not a cap — canvas.width = clientWidth * dpr would
  // grow the element, fire the observer, and loop.
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{ width: '100%', maxWidth: '100%' }}
    />
  );
};
